/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { call, delay, put, race } from "redux-saga/effects";

import * as azureBenefitApi from "Services/AzureBenefitApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IAzureBenefitApproveOrDeclineRequestDataResult } from "Interfaces/AzureBenefitRequests/IAzureBenefitApproveOrDeclineRequestDataResult";
import { IAzureBenefitRequestDetailsDataResult } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequestDetailsDataResult";
import { IAzureBenefitRequestDetailsResult } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequestDetailsResult";
import { IAzureBenefitRequestsDataResult } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequestsDataResult";
import { IAzureBenefitRequestsResult } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequestsResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import { IApiErrorResult, IApiResult2 } from "../../Interfaces/IApiResult";
import {
  approveOrDeclineAzureBenefitRequestFailure,
  approveOrDeclineAzureBenefitRequestSuccess,
  getAzureBenefitRequestDetailsFail,
  getAzureBenefitRequestDetailsSuccess,
  searchAzureBenefitRequestsFail,
  searchAzureBenefitRequestsSuccess,
  IGetAzureBenefitRequestDetails,
  ISearchAzureBenefitRequests,
} from "./AzureBenefitRequestsAction";

/**
 * Search all benefits for given text
 * @param {ISearchAzureBenefitRequests} action for searching benefits
 * @returns {void}
 */
function* handleSearchAzureBenefitRequests(action: ISearchAzureBenefitRequests) {
  const failedMessage = "Failed to search azure benefit requests.";
  const requestBody = {
    searchKey: action.searchText,
    pageIndex: action.pageIndex,
    pageSize: action.pageSize,
  } as unknown as any;

  action.selectedRequestsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case "expedited":
        requestBody.expedited = item.value;
        break;
    }
  });

  const apiEndpoint =
    action.tenant === TenantTypesEnum.MVP.toLowerCase()
      ? azureBenefitApi.searchMvpAzureBenefitRequests
      : azureBenefitApi.searchRdAzureBenefitRequests;

  try {
    const { result, timeout }: IApiResult2<IAzureBenefitRequestsDataResult> | any = yield race({
      result: call(apiEndpoint, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchAzureBenefitRequestsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IAzureBenefitRequestsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchAzureBenefitRequestsSuccess(
            parsedBody.requests,
            action.pageIndex,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          searchAzureBenefitRequestsFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(searchAzureBenefitRequestsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchAzureBenefitRequestsFail(error));
  }
}

/**
 * Get azure benefit request details.
 * @param {IGetAzureBenefitRequestDetails} action for getting azure benefit request details
 * @returns {void}
 */
function* handleGetAzureBenefitRequestDetails(action: IGetAzureBenefitRequestDetails) {
  const failedMessage = "Failed to get azure benefit request details.";

  const apiEndpoint =
    action.tenant === TenantTypesEnum.MVP.toLowerCase()
      ? azureBenefitApi.getMvpAzureRequestDetails
      : azureBenefitApi.getRdAzureRequestDetails;

  try {
    const { result, timeout }: IApiResult2<IAzureBenefitRequestDetailsDataResult> | any =
      yield race({
        result: call(apiEndpoint, action.userProfileId),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(getAzureBenefitRequestDetailsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IAzureBenefitRequestDetailsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getAzureBenefitRequestDetailsSuccess(
            parsedBody.questionAnswerMap,
            parsedBody.expedited,
            parsedBody?.expeditedDate
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getAzureBenefitRequestDetailsFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(getAzureBenefitRequestDetailsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getAzureBenefitRequestDetailsFail(error));
  }
}

/**
 * Approve or deny azure benefit request.
 * @param {IGetAzureBenefitRequestDetails} action for updating azure benefit request approved status.
 * @returns {void}
 */
function* handleApproveOrDenyAzureBenefitRequest(action: any) {
  const failedMessage = "Failed to approve or deny the azure benefit request.";

  const apiEndpoint =
    action.tenant === TenantTypesEnum.MVP.toLowerCase()
      ? azureBenefitApi.approveOrDeclineMvpAzureBenefitRequest
      : azureBenefitApi.approveOrDeclineRdAzureBenefitRequest;

  try {
    const { result, timeout }: IApiResult2<IAzureBenefitApproveOrDeclineRequestDataResult> | any =
      yield race({
        result: call(apiEndpoint, action),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(approveOrDeclineAzureBenefitRequestFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody; // result is either true or false

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(approveOrDeclineAzureBenefitRequestSuccess(parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          approveOrDeclineAzureBenefitRequestFailure(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(approveOrDeclineAzureBenefitRequestFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(approveOrDeclineAzureBenefitRequestFailure(error.message));
  }
}

export {
  handleApproveOrDenyAzureBenefitRequest,
  handleGetAzureBenefitRequestDetails,
  handleSearchAzureBenefitRequests,
};
